import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Link2, X } from "lucide-react";
import { create } from "zustand";
import { useAuth } from "@/data/auth";
import { useItem, useSpaceOf } from "@/data/store";
import { useFolderMeta } from "@/data/sharedFolders";
import { Button } from "@/ui/Button";
import { Input } from "@/ui/Input";
import { IconButton } from "@/ui/IconButton";
import { toast } from "@/ui/toast";
import s from "./SignInDialog.module.css";

interface ShareDialogStore {
  noteId: string | null;
  open: (noteId: string) => void;
  close: () => void;
}
export const useShareDialog = create<ShareDialogStore>((set) => ({
  noteId: null,
  open: (noteId) => set({ noteId }),
  close: () => set({ noteId: null }),
}));

export function ShareDialog() {
  const { noteId, close } = useShareDialog();
  const item = useItem(noteId);
  const space = useSpaceOf(noteId);
  const folderMeta = useFolderMeta(space?.kind === "shared" ? space.spaceId : null);
  // publishing shared-folder content to the open internet is an ownership call
  const mayShare = space?.kind !== "shared" || folderMeta?.role === "owner";
  const spaceQuery = space?.kind === "shared" ? `?spaceId=${encodeURIComponent(space.spaceId)}` : "";
  const signedIn = useAuth((a) => a.status === "signedIn");
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!noteId || !signedIn || !mayShare) {
      setUrl(null);
      return;
    }
    void fetch(`/api/notes/${noteId}/share${spaceQuery}`)
      .then((r) => (r.ok ? r.json() : { url: null }))
      .then((d: { url: string | null }) => setUrl(d.url ? absolute(d.url) : null));
  }, [noteId, signedIn, mayShare, spaceQuery]);

  const createLink = async () => {
    if (!noteId || busy) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/notes/${noteId}/share${spaceQuery}`, { method: "POST" });
      const data = (await res.json()) as { url?: string; error?: string };
      if (!res.ok || !data.url) throw new Error(data.error ?? "share failed");
      const full = absolute(data.url);
      setUrl(full);
      await navigator.clipboard.writeText(full).catch(() => {});
      toast.success("Share link created and copied");
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!noteId) return;
    await fetch(`/api/notes/${noteId}/share${spaceQuery}`, { method: "DELETE" });
    setUrl(null);
    toast.info("Share link revoked. The page is no longer reachable.");
  };

  return (
    <Dialog.Root open={noteId !== null} onOpenChange={(o) => !o && close()}>
      <Dialog.Portal>
        <Dialog.Overlay className={s.overlay} />
        <Dialog.Content className={s.dialog}>
          <div className={s.header}>
            <Link2 size={20} />
            <Dialog.Title className={s.title}>Share "{item?.title || "Untitled"}"</Dialog.Title>
          </div>
          {!signedIn ? (
            <Dialog.Description className={s.subtitle}>
              Sharing needs cloud sync. Sign in first.
            </Dialog.Description>
          ) : !mayShare ? (
            <Dialog.Description className={s.subtitle}>
              Only the folder owner can publish notes from a shared folder.
            </Dialog.Description>
          ) : url ? (
            <>
              <Dialog.Description className={s.subtitle}>
                Anyone with this link can read the note (latest synced version, read-only).
              </Dialog.Description>
              <div className={s.form}>
                <Input readOnly value={url} onFocus={(e) => e.target.select()} />
                <Button
                  variant="solid"
                  onClick={() => {
                    void navigator.clipboard.writeText(url);
                    toast.success("Link copied");
                  }}
                >
                  Copy link
                </Button>
                <Button variant="ghost" onClick={() => void revoke()}>
                  Revoke link
                </Button>
              </div>
            </>
          ) : (
            <>
              <Dialog.Description className={s.subtitle}>
                Create a public read-only page for this note. It shows the latest synced version and
                can be revoked any time.
              </Dialog.Description>
              <Button variant="solid" disabled={busy} onClick={() => void createLink()}>
                {busy ? "Creating…" : "Create share link"}
              </Button>
            </>
          )}
          <Dialog.Close asChild>
            <IconButton label="Close" className={s.close}>
              <X size={14} />
            </IconButton>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function absolute(path: string): string {
  return `${location.origin}${path}`;
}
